import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Users, Settings, Shield, LogOut, Package,
  Menu, X, Calendar, Wrench, DollarSign, Download, Inbox, ClipboardList,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Panel", icon: LayoutDashboard, perm: null, testid: "nav-dashboard" },
  { to: "/prospects", label: "Prospectos", icon: Inbox, perm: null, roles: ["admin", "vendedor"], testid: "nav-prospects" },
  { to: "/quotes", label: "Cotizaciones", icon: FileText, perm: "quotes_read", testid: "nav-quotes" },
  { to: "/clients", label: "Clientes", icon: Users, perm: "clients_read", testid: "nav-clients" },
  { to: "/calendar", label: "Agenda", icon: Calendar, perm: "calendar_read", testid: "nav-calendar" },
  { to: "/reports", label: "Reportes", icon: Wrench, perm: "reports_read", testid: "nav-reports" },
  { to: "/finance", label: "Finanzas", icon: DollarSign, perm: "finance_read", testid: "nav-finance" },
  { to: "/catalog", label: "Catálogo", icon: Package, perm: "catalog_read", testid: "nav-catalog" },
  { to: "/prompts", label: "Prompts", icon: ClipboardList, perm: null, testid: "nav-prompts-notes" },
  { to: "/users", label: "Usuarios", icon: Shield, perm: "users_manage", testid: "nav-users" },
  { to: "/company", label: "Empresa", icon: Settings, perm: "company_config", testid: "nav-company" },
];

export default function AppLayout() {
  const { user, logout, can } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const visibleItems = NAV_ITEMS.filter((n) => {
    if (n.roles && !n.roles.includes(user?.role)) return false;
    return !n.perm || can(n.perm);
  });

  const handleBackup = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/backup/export`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("pe_token")}` },
      });
      if (!r.ok) throw new Error("Descarga falló");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m?.[1] || "power_cotizador_backup.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Error al descargar respaldo: " + e.message);
    }
  };

  const Sidebar = (
    <aside
      className="h-full w-64 shrink-0 flex flex-col text-slate-100"
      style={{ backgroundColor: "var(--pe-primary)" }}
    >
      <div className="px-5 py-5 border-b border-white/10">
        <Logo height={44} dark />
        <div className="mt-1.5 text-[10px] font-black tracking-[0.15em] text-white/80">
          SOLUCIONES DE VANGUARDIA
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {visibleItems.map((n) => {
          const Icon = n.icon;
          if (n.disabled) {
            return (
              <div key={n.to} className="pe-nav-item opacity-40 cursor-not-allowed" title="Próximamente"
                   data-testid={n.testid}>
                <Icon size={18} />
                <span>{n.label}</span>
                <span className="ml-auto text-[9px] uppercase tracking-widest opacity-70">Pronto</span>
              </div>
            );
          }
          return (
            <NavLink key={n.to} to={n.to}
              className={({ isActive }) => `pe-nav-item ${isActive ? "active" : ""}`}
              onClick={() => setMobileOpen(false)} data-testid={n.testid}>
              <Icon size={18} />
              <span>{n.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-2">
        {can("backup") && (
          <button onClick={handleBackup} className="pe-nav-item w-full" data-testid="btn-backup">
            <Download size={18} />
            <span>Respaldo ZIP</span>
          </button>
        )}
        <div className="px-3 pt-2 pb-1 text-xs text-white/60 truncate">
          {user?.name}
          <div className="text-[10px] uppercase tracking-widest text-white/40">{user?.role}</div>
        </div>
        <button onClick={logout} className="pe-nav-item w-full text-red-200 hover:text-red-100" data-testid="btn-logout">
          <LogOut size={18} />
          <span>Cerrar sesión</span>
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-[color:var(--pe-bg)]">
      <div className="hidden md:flex">{Sidebar}</div>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative h-full">{Sidebar}</div>
        </div>
      )}
      <div className="flex flex-1 flex-col min-w-0">
        <header className="h-14 bg-white border-b border-[color:var(--pe-border)] flex items-center px-4 gap-3">
          <button className="md:hidden p-2 rounded-md hover:bg-slate-100"
                  onClick={() => setMobileOpen(!mobileOpen)} data-testid="btn-mobile-menu">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="flex-1 flex items-center gap-3">
            <div className="hidden md:block text-[11px] font-bold tracking-[0.16em] text-[color:var(--pe-primary)]">
              POWER COTIZADOR
            </div>
          </div>
          {can("quotes_write") && (
            <Button size="sm" onClick={() => navigate("/quotes/new")}
                    className="pe-btn-primary text-white border-transparent hover:text-white"
                    data-testid="btn-new-quote-header">
              + Nueva cotización
            </Button>
          )}
        </header>
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
